import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, X, Check, Shield, Users as UsersIcon, Briefcase, Ban, RotateCw, KeyRound, Copy, CheckCheck } from 'lucide-react';

import { api } from '../../api/client.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import './UsersPanel.css';

const ROLE_META = {
    admin:  { label: 'Admin',  Icon: Shield,    description: 'Full access, manages users and settings.' },
    team:   { label: 'Team',   Icon: UsersIcon, description: 'Read + write on all investors and deals.' },
    client: { label: 'Client', Icon: Briefcase, description: 'Read-only, restricted to assigned portfolio companies.' },
};

export function UsersPanel() {
    const queryClient = useQueryClient();
    const { user: currentUser } = useAuth();
    const [inviting, setInviting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    // Reset password state
    const [resetConfirmUser, setResetConfirmUser] = useState(null);  // user object awaiting confirmation
    const [resetResult, setResetResult] = useState(null);            // { userName, newPassword }
    const [copied, setCopied] = useState(false);

    const resetPasswordMutation = useMutation({
        mutationFn: (userId) => api.post(`/users/${userId}/reset-password`, {}),
        onSuccess: (data, userId) => {
            const u = (users || []).find(x => x.id === userId);
            setResetResult({ userName: u?.name || u?.email || `User #${userId}`, newPassword: data.newPassword });
            setResetConfirmUser(null);
        },
    });

    const handleCopyPassword = () => {
        if (!resetResult) return;
        navigator.clipboard.writeText(resetResult.newPassword).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleDismissResult = () => {
        setResetResult(null);
        setCopied(false);
    };

    const { data: users, isLoading, error } = useQuery({
        queryKey: ['users'],
        queryFn: () => api.get('/auth/users'),
    });

    const { data: startups } = useQuery({
        queryKey: ['startups'],
        queryFn: () => api.get('/startups?include_inactive=true'),
    });

    const createMutation = useMutation({
        mutationFn: (data) => api.post('/auth/users', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setInviting(false);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, updates }) => api.put(`/auth/users/${id}`, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setEditingId(null);
        },
    });

    const deactivateMutation = useMutation({
        mutationFn: (id) => api.delete(`/auth/users/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
    });

    if (isLoading) return <div className="panel-empty"><span className="spinner" /></div>;
    if (error) return <div className="panel-error">Couldn't load users: {error.message}</div>;

    const active   = users.filter(u => u.is_active);
    const inactive = users.filter(u => !u.is_active);

    return (
        <div>
            <div className="panel-section">
                <div className="panel-section-header">
                    <div>
                        <h2 className="panel-section-title">Team &amp; clients</h2>
                        <p className="panel-section-subtitle">
                            {active.length} active user{active.length === 1 ? '' : 's'}
                            {inactive.length > 0 && ` · ${inactive.length} deactivated`}
                        </p>
                    </div>
                    {!inviting && (
                        <button className="btn-primary-small" onClick={() => setInviting(true)}>
                            <UserPlus size={14} strokeWidth={2} /> Invite user
                        </button>
                    )}
                </div>

                {inviting && (
                    <InviteForm
                        startups={startups || []}
                        onCancel={() => setInviting(false)}
                        onSubmit={(data) => createMutation.mutate(data)}
                        submitting={createMutation.isPending}
                        error={createMutation.error}
                    />
                )}

                <ul className="user-list">
                    {active.map(u => (
                        <UserRow
                            key={u.id}
                            user={u}
                            startups={startups || []}
                            isCurrent={u.id === currentUser?.id}
                            editing={editingId === u.id}
                            onEdit={() => setEditingId(u.id)}
                            onCancel={() => setEditingId(null)}
                            onSave={(updates) => updateMutation.mutate({ id: u.id, updates })}
                            onDeactivate={() => {
                                if (confirm(`Deactivate ${u.email}? They won't be able to log in anymore.`)) {
                                    deactivateMutation.mutate(u.id);
                                }
                            }}
                            onResetPassword={() => setResetConfirmUser(u)}
                            saving={updateMutation.isPending && updateMutation.variables?.id === u.id}
                        />
                    ))}
                </ul>
            </div>

            {inactive.length > 0 && (
                <div className="panel-section">
                    <h2 className="panel-section-title">Deactivated ({inactive.length})</h2>
                    <ul className="user-list">
                        {inactive.map(u => (
                            <li key={u.id} className="user-row user-row-inactive">
                                <div className="user-avatar">{initials(u.name || u.email)}</div>
                                <div className="user-info">
                                    <div className="user-name">{u.name || u.email}</div>
                                    <div className="user-meta">{u.email} · deactivated</div>
                                </div>
                                <button
                                    className="user-action-btn"
                                    title="Reactivate"
                                    onClick={() => updateMutation.mutate({ id: u.id, updates: { is_active: 1 } })}
                                >
                                    <RotateCw size={13} strokeWidth={1.75} />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Reset Password — confirmation modal */}
            {resetConfirmUser && (
                <div className="modal-overlay" onClick={() => setResetConfirmUser(null)}>
                    <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <header className="modal-header">
                            <h2 className="modal-title">Reset password?</h2>
                        </header>
                        <div className="modal-body">
                            <p>
                                Generate a new password for <strong>{resetConfirmUser.name || resetConfirmUser.email}</strong>?
                                They will be required to set a new password on next login.
                            </p>
                            {resetPasswordMutation.isError && (
                                <div className="panel-error">{resetPasswordMutation.error.message}</div>
                            )}
                            <div className="modal-actions">
                                <button className="btn-ghost" onClick={() => setResetConfirmUser(null)}
                                        disabled={resetPasswordMutation.isPending}>
                                    <X size={14} strokeWidth={2}/> Cancel
                                </button>
                                <button className="btn-save"
                                        onClick={() => resetPasswordMutation.mutate(resetConfirmUser.id)}
                                        disabled={resetPasswordMutation.isPending}>
                                    {resetPasswordMutation.isPending
                                        ? <span className="spinner"/>
                                        : <><KeyRound size={14} strokeWidth={2}/> Generate password</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Password — result modal */}
            {resetResult && (
                <div className="modal-overlay">
                    <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <header className="modal-header">
                            <h2 className="modal-title">New password generated</h2>
                        </header>
                        <div className="modal-body">
                            <p>Password for <strong>{resetResult.userName}</strong>:</p>
                            <div className="reset-password-display">
                                <code className="reset-password-code">{resetResult.newPassword}</code>
                            </div>
                            <button className="btn-ghost reset-copy-btn" onClick={handleCopyPassword}>
                                {copied
                                    ? <><CheckCheck size={14} strokeWidth={2}/> Copied!</>
                                    : <><Copy size={14} strokeWidth={2}/> Copy to clipboard</>}
                            </button>
                            <div className="reset-password-warning">
                                <strong>This password will only be shown once.</strong>{' '}
                                Copy and send it to the user via a secure channel.
                            </div>
                            <div className="modal-actions">
                                <button className="btn-primary-small" onClick={handleDismissResult}>
                                    <Check size={14} strokeWidth={2}/> Done
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
function UserRow({ user, startups, isCurrent, editing, onEdit, onCancel, onSave, onDeactivate, onResetPassword, saving }) {
    const meta = ROLE_META[user.role] || ROLE_META.team;
    const { Icon: RoleIcon } = meta;

    if (editing) {
        return (
            <li className="user-row user-row-editing">
                <UserEditForm
                    user={user}
                    startups={startups}
                    onSave={onSave}
                    onCancel={onCancel}
                    saving={saving}
                />
            </li>
        );
    }

    const startupNames = user.startup_access
        ? user.startup_access.split(',').map(id => {
              const s = startups.find(s => s.id === Number(id));
              return s?.name;
          }).filter(Boolean)
        : [];

    return (
        <li className="user-row">
            <div className="user-avatar">{initials(user.name || user.email)}</div>

            <div className="user-info">
                <div className="user-name">
                    {user.name || user.email}
                    {isCurrent && <span className="user-you">You</span>}
                </div>
                <div className="user-meta">
                    {user.email}
                    {user.last_login_at && ` · last login ${formatRelative(user.last_login_at)}`}
                </div>
            </div>

            <div className={`user-role-pill user-role-${user.role}`}>
                <RoleIcon size={11} strokeWidth={1.75} />
                {meta.label}
            </div>

            {user.role === 'client' && (
                <div className="user-startups" title={startupNames.join(', ') || 'No startups assigned'}>
                    {startupNames.length === 0
                        ? <span className="user-startups-empty">No access</span>
                        : startupNames.length === 1
                            ? startupNames[0]
                            : `${startupNames[0]} +${startupNames.length - 1}`}
                </div>
            )}

            <div className="user-actions">
                <button className="user-action-btn" onClick={onEdit} title="Edit">
                    Edit
                </button>
                {!isCurrent && (
                    <button className="user-action-btn" onClick={onResetPassword} title="Reset password">
                        <KeyRound size={13} strokeWidth={1.75} />
                    </button>
                )}
                {!isCurrent && (
                    <button className="user-action-btn user-action-danger"
                            onClick={onDeactivate} title="Deactivate user">
                        <Ban size={13} strokeWidth={1.75} />
                    </button>
                )}
            </div>
        </li>
    );
}

/* -------------------------------------------------------------------------- */
function InviteForm({ startups, onCancel, onSubmit, submitting, error }) {
    const [form, setForm] = useState({
        email: '',
        name: '',
        password: generatePassword(),
        role: 'team',
        startup_ids: [],
    });
    const [showPwd, setShowPwd] = useState(true);

    const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    const toggleStartup = (id) => {
        setForm(prev => ({
            ...prev,
            startup_ids: prev.startup_ids.includes(id)
                ? prev.startup_ids.filter(x => x !== id)
                : [...prev.startup_ids, id],
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            email: form.email.trim(),
            name: form.name.trim() || null,
            password: form.password,
            role: form.role,
            startup_access: form.role === 'client' && form.startup_ids.length
                ? form.startup_ids.join(',')
                : null,
        };
        onSubmit(payload);
    };

    return (
        <form className="invite-form" onSubmit={handleSubmit}>
            <div className="invite-grid">
                <label className="detail-field">
                    <span className="detail-field-label">Email *</span>
                    <input type="email" required value={form.email} onChange={set('email')}
                           className="detail-field-input" placeholder="person@company.com"/>
                </label>
                <label className="detail-field">
                    <span className="detail-field-label">Name</span>
                    <input type="text" value={form.name} onChange={set('name')}
                           className="detail-field-input" placeholder="Full name (optional)"/>
                </label>
                <label className="detail-field">
                    <span className="detail-field-label">Role *</span>
                    <select value={form.role} onChange={set('role')} className="detail-field-input">
                        {Object.entries(ROLE_META).map(([k, v]) => (
                            <option key={k} value={k}>{v.label} — {v.description}</option>
                        ))}
                    </select>
                </label>
                <label className="detail-field">
                    <span className="detail-field-label">
                        Temporary password *
                        <button type="button" className="regen-link"
                                onClick={() => setForm(p => ({ ...p, password: generatePassword() }))}>
                            regenerate
                        </button>
                    </span>
                    <div className="pwd-row">
                        <input type={showPwd ? 'text' : 'password'} required
                               value={form.password} onChange={set('password')}
                               className="detail-field-input mono" minLength={10}/>
                        <button type="button" className="pwd-toggle"
                                onClick={() => setShowPwd(!showPwd)}>
                            {showPwd ? 'hide' : 'show'}
                        </button>
                    </div>
                </label>
            </div>

            {form.role === 'client' && (
                <div className="startup-access-picker">
                    <label className="detail-field-label">
                        Startup access — which portfolio companies can this client see?
                    </label>
                    {startups.length === 0 ? (
                        <p className="startup-access-empty">No startups available. Add some in the Portfolio tab first.</p>
                    ) : (
                        <div className="startup-checkboxes">
                            {startups.map(s => (
                                <label key={s.id} className={`startup-checkbox${form.startup_ids.includes(s.id) ? ' is-checked' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={form.startup_ids.includes(s.id)}
                                        onChange={() => toggleStartup(s.id)}
                                    />
                                    <span>{s.name}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <p className="invite-note mono">
                Share this password with the user through a secure channel. They can change it after first login.
            </p>

            {error && <div className="panel-error">{error.message}</div>}

            <div className="invite-actions">
                <button type="button" className="btn-ghost" onClick={onCancel} disabled={submitting}>
                    <X size={14} strokeWidth={2}/> Cancel
                </button>
                <button type="submit" className="btn-save" disabled={submitting}>
                    {submitting ? <span className="spinner"/> : <><Check size={14} strokeWidth={2}/> Create user</>}
                </button>
            </div>
        </form>
    );
}

/* -------------------------------------------------------------------------- */
function UserEditForm({ user, startups, onSave, onCancel, saving }) {
    const [form, setForm] = useState({
        name: user.name || '',
        role: user.role,
        startup_ids: user.startup_access
            ? user.startup_access.split(',').map(Number).filter(Boolean)
            : [],
    });

    const toggleStartup = (id) => {
        setForm(prev => ({
            ...prev,
            startup_ids: prev.startup_ids.includes(id)
                ? prev.startup_ids.filter(x => x !== id)
                : [...prev.startup_ids, id],
        }));
    };

    const handleSave = () => {
        const updates = {
            name: form.name.trim() || null,
            role: form.role,
            startup_access: form.role === 'client' && form.startup_ids.length
                ? form.startup_ids.join(',')
                : null,
        };
        onSave(updates);
    };

    return (
        <div className="invite-form" style={{ width: '100%' }}>
            <div className="invite-grid">
                <label className="detail-field">
                    <span className="detail-field-label">Name</span>
                    <input type="text" value={form.name}
                           onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                           className="detail-field-input"/>
                </label>
                <label className="detail-field">
                    <span className="detail-field-label">Role</span>
                    <select value={form.role}
                            onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))}
                            className="detail-field-input">
                        {Object.entries(ROLE_META).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                </label>
            </div>

            {form.role === 'client' && (
                <div className="startup-access-picker">
                    <label className="detail-field-label">Startup access</label>
                    <div className="startup-checkboxes">
                        {startups.map(s => (
                            <label key={s.id} className={`startup-checkbox${form.startup_ids.includes(s.id) ? ' is-checked' : ''}`}>
                                <input type="checkbox" checked={form.startup_ids.includes(s.id)}
                                       onChange={() => toggleStartup(s.id)}/>
                                <span>{s.name}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            <div className="invite-actions">
                <button type="button" className="btn-ghost" onClick={onCancel} disabled={saving}>
                    <X size={14} strokeWidth={2}/> Cancel
                </button>
                <button type="button" className="btn-save" onClick={handleSave} disabled={saving}>
                    {saving ? <span className="spinner"/> : <><Check size={14} strokeWidth={2}/> Save</>}
                </button>
            </div>
        </div>
    );
}

/* ========================================================================== */
function initials(s) {
    if (!s) return '?';
    const parts = s.trim().split(/[\s@]/).filter(Boolean).slice(0, 2);
    return parts.map(p => p[0]).join('').toUpperCase();
}

function generatePassword() {
    // 12 char password with mixed case, digits, and a few special chars
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let out = '';
    for (let i = 0; i < 14; i++) {
        out += chars[Math.floor(Math.random() * chars.length)];
    }
    return out;
}

function formatRelative(iso) {
    const d = new Date(iso);
    const diffDays = Math.floor((Date.now() - d) / 86_400_000);
    if (diffDays < 1) return 'today';
    if (diffDays < 30) return `${diffDays}d ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
}
